import { useState } from "react";
import { Link } from "react-router-dom";
import { Arr } from "../../components/CTA";

/* ───────────────────────── Interactive comparison ─────────────────────── */

const STATES = {
  generic: {
    rows: [
      { k: "Invoice number", v: "INV-20418", c: "98%", s: "ok" },
      { k: "Vendor", v: "Acme Supplies Ltd", c: "95%", s: "ok" },
      {
        k: "Total amount",
        v: "GBP 10,400.00",
        c: "92%",
        s: "wrong",
        note: "This value is the subtotal, not the total. The model does not flag it.",
      },
      { k: "Due date", v: "30 April 2026", c: "88%", s: "ok" },
      {
        k: "Project code",
        v: "no value",
        c: "n/a",
        s: "na",
        note: "No field for this in the generic schema.",
      },
    ],
    summary:
      "Five fields, every box filled, nothing flagged. The total is wrong, and the result gives no sign of it.",
  },
  trained: {
    rows: [
      { k: "Invoice number", v: "INV-20418", c: "99.1%", s: "ok" },
      { k: "Vendor", v: "Acme Supplies Ltd", c: "98.4%", s: "ok" },
      {
        k: "Total amount",
        v: "GBP 12,480.00",
        c: "64%",
        s: "flag",
        note: "Low confidence, sent to a reviewer.",
      },
      { k: "Due date", v: "30 April 2026", c: "96.5%", s: "ok" },
      {
        k: "Project code",
        v: "PRJ-7741",
        c: "95.0%",
        s: "ok",
        note: "Trained for this custom field.",
      },
    ],
    summary:
      "Every value correct. The one genuinely ambiguous field is flagged for a person, not guessed. Your custom field is here, because you trained for it.",
  },
};

function StatusIcon({ s }) {
  if (s === "ok")
    return (
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="cmp__sicon cmp__sicon--ok">
        <path d="M3 8.5l3.2 3.2L13 4.6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  if (s === "wrong")
    return (
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="cmp__sicon cmp__sicon--wrong">
        <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  if (s === "flag")
    return (
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="cmp__sicon cmp__sicon--flag">
        <path d="M4 14V3h7l-1.4 2.5L11 8H4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
    );
  return null;
}

function Comparison() {
  const [mode, setMode] = useState("generic");
  const data = STATES[mode];

  return (
    <aside className="cmp" aria-labelledby="cmp-title" data-testid="comparison-widget">
      <header className="cmp__head">
        <h3 id="cmp-title" className="cmp__title">The same document, two ways</h3>
        <p className="cmp__intro">
          One invoice. Switch between a generic model and a trained model, and watch the extracted fields change.
        </p>
      </header>

      {/* Toggle */}
      <div className="cmp__toggle" role="tablist" aria-label="Model type">
        <button
          role="tab"
          aria-selected={mode === "generic"}
          className={`cmp__opt${mode === "generic" ? " is-active" : ""}`}
          onClick={() => setMode("generic")}
          data-testid="toggle-generic"
        >
          Generic model
        </button>
        <button
          role="tab"
          aria-selected={mode === "trained"}
          className={`cmp__opt${mode === "trained" ? " is-active" : ""}`}
          onClick={() => setMode("trained")}
          data-testid="toggle-trained"
        >
          Trained model
        </button>
        <span className={`cmp__slider cmp__slider--${mode}`} aria-hidden="true" />
      </div>

      {/* Two-column body */}
      <div className="cmp__grid">
        {/* Left: invoice preview (static) */}
        <div className="cmp__doc" aria-label="Invoice document">
          <div className="cmp__doc-top">
            <div>
              <b className="cmp__doc-vendor">Acme Supplies Ltd</b>
              <div className="cmp__doc-sub">Industrial fasteners and supply</div>
            </div>
            <div className="cmp__doc-stamp">Invoice</div>
          </div>
          <div className="cmp__doc-meta">
            <div><span>Invoice no.</span><b>INV-20418</b></div>
            <div><span>Invoice date</span><b>12 March 2026</b></div>
          </div>
          <table className="cmp__doc-table">
            <thead>
              <tr><th>Description</th><th className="num">GBP</th></tr>
            </thead>
            <tbody>
              <tr><td>Industrial fasteners, bulk</td><td className="num">6,200.00</td></tr>
              <tr><td>Freight and handling</td><td className="num">4,200.00</td></tr>
            </tbody>
          </table>
          <div className="cmp__doc-totals">
            <div><span>Subtotal</span><b>GBP 10,400.00</b></div>
            <div><span>VAT at 20%</span><b>GBP 2,080.00</b></div>
            <div className="cmp__doc-total"><span>Total due</span><b>GBP 12,480.00</b></div>
            <div className="cmp__doc-due"><span>Due date</span><b>30 April 2026</b></div>
          </div>
        </div>

        {/* Right: extracted fields (animates) */}
        <div className="cmp__panel">
          <div className="cmp__panel-head">
            <span>Extracted fields</span>
            <span className="cmp__panel-tag">{mode === "generic" ? "Generic schema" : "Acme · trained model"}</span>
          </div>
          <div className="cmp__rows" key={mode} data-testid={`fields-${mode}`}>
            {data.rows.map((r) => (
              <div className={`cmp__row cmp__row--${r.s}`} key={r.k}>
                <div className="cmp__cell-k">{r.k}</div>
                <div className="cmp__cell-v">
                  <span className={`cmp__val${r.s === "na" ? " cmp__val--na" : ""}`}>{r.v}</span>
                  {r.note && <span className="cmp__note">{r.note}</span>}
                </div>
                <div className="cmp__cell-c">
                  <StatusIcon s={r.s} />
                  <span className="cmp__conf">{r.c}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="cmp__summary" data-testid={`summary-${mode}`}>{data.summary}</p>
        </div>
      </div>

      <p className="cmp__caption">
        Same document. The generic model fills every box and flags nothing, including the box it got wrong. The trained model is correct, is honest about the one hard field, and has the field your business actually needs.
      </p>
      <p className="cmp__foot">
        An illustration, not a benchmark. The structural point holds: a fixed-schema model cannot decline, and cannot return a field it was never built for.
      </p>
    </aside>
  );
}

/* ────────────────────────────── Page ────────────────────────────── */

export default function TrainedVsGeneric() {
  return (
    <article className="article">
      {/* Header */}
      <header className="article__head">
        <div className="container container--narrow">
          <nav className="crumb" aria-label="Breadcrumb">
            <Link to="/">Docket</Link> / <Link to="/insights">Insights</Link> / Trained vs. generic IDP
          </nav>
          <p className="article__eyebrow">Point of view &nbsp;·&nbsp; December 2025 &nbsp;·&nbsp; 5 min read</p>
          <h1 className="article__title">Trained vs. generic IDP: why the model you build always wins</h1>
          <p className="article__standfirst">
            Generic models guess. Trained models say what they know. Here is what that difference does to your accuracy curve.
          </p>
          <hr className="article__rule" />
        </div>
      </header>

      {/* Body */}
      <div className="container container--narrow article__body">

        <p>
          "Works out of the box" is the most reassuring phrase in enterprise software, and for document AI it is the one to treat with the most caution.
        </p>
        <p>
          The promise of generic intelligent document processing is genuinely appealing. Point a pre-trained model at an invoice, a contract or a claim, and it returns structured data. No training, no setup, no examples to gather. For a certain kind of document it does exactly that, and it does it well.
        </p>
        <p>
          But "works on any document" and "works well on your documents" are two different claims. The space between them is where the cost hides, and it is worth being precise about where that space opens up.
        </p>

        <h2 className="article__sub">What a generic model actually is</h2>
        <p>
          A generic IDP model is trained once, by a vendor, on an enormous and varied pile of documents. It learns the shape of an average invoice, an average purchase order, an average form. Then it is shipped to everyone, unchanged.
        </p>
        <p>
          That is a real achievement, and for genuinely standard, low-variation documents it is often all you need. If your documents look like the average document the vendor trained on, a generic model will read them competently from day one. We would not pretend otherwise.
        </p>
        <p>
          The difficulty is simple. You do not process the average invoice. You process your suppliers' invoices.
        </p>

        <h2 className="article__sub">The average-document problem</h2>
        <p>
          A generic model is optimised to be good at everyone's documents at once. That sounds like strength. In practice it means the model is competent everywhere and excellent nowhere. It is tuned for a spread of documents that is not your spread.
        </p>
        <p>
          Your reality is narrower and stranger than the average. It is forty vendors who each lay out a total a little differently. It is the one form your largest customer insists on using. It is the field your finance team depends on that no general schema has ever heard of. A model trained on the average has no particular reason to be good at any of that.
        </p>
        <p>
          A model trained on your documents has every reason to be. That is the whole of the difference, and it shows up in two places: what the model does when it is unsure, and what happens to its accuracy over time.
        </p>

        <h2 className="article__sub">The difference that costs money</h2>
        <p>
          A generic model is built to return an answer. For every field in its schema, on every document, it produces a value and a confidence number. That is by design. It is not built to look at a document and decline.
        </p>
        <p>
          So when a generic model meets something it has not really seen, an unusual supplier layout, a document type outside its training, a scan that is simply ambiguous, it does not stop. It guesses. And it reports that guess with a confidence score that looks no different from the scores on the fields it read correctly.
        </p>
        <p>
          This is the failure mode that matters. Not a blank, and not an error, but a confident wrong answer. A blank gets noticed. An error gets noticed. A plausible, well-formatted, confidently scored wrong value does not get noticed. It gets trusted. It flows into your system of record, and the cost of finding it three months later is many times the cost of catching it now.
        </p>
        <p>
          A model that is trained and scoped to known document types can do something a generic model structurally cannot. It can recognise that a document is not one it was trained to read, and say so. Docket calls that state "No model match," and it is not a limitation, it is the point. A model that knows the edge of what it knows is worth more than a model that answers everything.
        </p>
        <p>
          Generic models guess. Trained models say what they know, and, just as importantly, say when they do not.
        </p>

        <Comparison />

        <h2 className="article__sub">A flat line versus a curve</h2>
        <p>The second place the gap opens is over time.</p>
        <p>
          A generic model is, for you, essentially static. It is about as accurate on your documents in month twelve as in month one. The vendor does keep improving it, but on their schedule, against their average, for all of their customers at once. None of that improvement is aimed at your documents in particular.
        </p>
        <p>
          A trained model sits on a different path. Every correction a reviewer makes is not only a fix to one document, it is a piece of training signal. The model gets better at the documents you actually process, because the documents you actually process are what it learns from. Its accuracy is a curve that climbs toward your reality.
        </p>
        <p>
          Plot the two and the shape of the decision becomes clear. Generic accuracy is a flat line, set by someone else, somewhere else. Trained accuracy is a slope, and the slope is yours. On the day you buy, you are not really choosing between two accuracy numbers. You are choosing between a number that will not move and a number that will.
        </p>

        <h2 className="article__sub">What to ask instead</h2>
        <p>
          When you evaluate document automation, the useful question is not how accurate it is. An accuracy figure with no document attached is a marketing number. Ask instead:
        </p>
        <ul className="article__list">
          <li><strong>Accurate on what.</strong> On the vendor's benchmark, or on documents that look like the ones you actually receive.</li>
          <li><strong>What does it do when it does not know.</strong> Does it guess and score the guess, or can it decline and hand the document to a person.</li>
          <li><strong>Whose accuracy improves when my team corrects it.</strong> Yours, or everyone's shared average, or nobody's.</li>
        </ul>
        <p>
          If a tool cannot decline, and cannot learn from your corrections, then what you are buying is a rented view of someone else's average. It may be a perfectly good average. Just be clear that it is not yours, and it is not going to become yours.
        </p>

        <h2 className="article__sub">The honest version</h2>
        <p>
          None of this means generic is the wrong choice for everyone. If your documents are genuinely standardised, and the cost of a wrong field is low, a generic model may be all you ever need, and training your own would be effort you do not have to spend. Buying more capability than your documents require is its own kind of waste.
        </p>
        <p>
          But most document operations are not that tidy. They are full of specific vendors, specific layouts, specific fields and specific edge cases. For those, a model built on your documents does not merely edge ahead. It starts ahead, because it was built for your reality, and it pulls further ahead each month, because it keeps learning from it. That is what "the model you build always wins" actually means. Not a slogan, but a starting point and a slope, both pointed at your documents.
        </p>

        {/* Closing CTA */}
        <section className="article__cta" data-testid="article-cta">
          <div>
            <h3>See a trained model on your own documents.</h3>
            <p>A Docket pilot trains on two or three of your real document types and shows you the accuracy on your data, not on a benchmark, in four to six weeks.</p>
          </div>
          <Link to="/contact" className="article__cta-btn" data-testid="article-cta-btn">
            Request a pilot <Arr />
          </Link>
        </section>

        {/* Back link */}
        <div className="article__backwrap">
          <Link to="/insights" className="txtlink" data-testid="article-back">
            <svg className="arr" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ transform: "rotate(180deg)" }}>
              <path d="M3 8h9m0 0L8 4m4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to all Insights
          </Link>
        </div>
      </div>
    </article>
  );
}
